import { useCallback, useEffect, useState } from "react";
import { RoomEvent, type MatrixClient, type Room } from "matrix-js-sdk";
import { getRoomMessages, markRoomRead, paginateBack, type ChatMessage } from "../../matrix/timeline";

export interface RoomTimelineState {
  messages: ChatMessage[];
  loadingOlder: boolean;
  canLoadMore: boolean;
  loadOlder: () => void;
}

/**
 * Subscribes to a room's live timeline, exposes the current messages, and
 * provides scrollback pagination. Marks the room read on new live events.
 */
export function useRoomTimeline(client: MatrixClient, room: Room): RoomTimelineState {
  const [messages, setMessages] = useState<ChatMessage[]>(() => getRoomMessages(client, room));
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [canLoadMore, setCanLoadMore] = useState(true);

  useEffect(() => {
    setMessages(getRoomMessages(client, room));
    setCanLoadMore(true);
    void markRoomRead(client, room);

    const onTimeline = (
      _event: unknown,
      eventRoom: Room | undefined,
      toStartOfTimeline: boolean | undefined,
    ) => {
      if (eventRoom?.roomId !== room.roomId) return;
      setMessages(getRoomMessages(client, room));
      if (!toStartOfTimeline) void markRoomRead(client, room);
    };

    client.on(RoomEvent.Timeline, onTimeline);
    return () => {
      client.removeListener(RoomEvent.Timeline, onTimeline);
    };
  }, [client, room]);

  const loadOlder = useCallback(() => {
    if (loadingOlder || !canLoadMore) return;
    setLoadingOlder(true);
    paginateBack(client, room, 30)
      .then((more) => {
        setMessages(getRoomMessages(client, room));
        setCanLoadMore(more);
      })
      .catch(() => setCanLoadMore(false))
      .finally(() => setLoadingOlder(false));
  }, [client, room, loadingOlder, canLoadMore]);

  return { messages, loadingOlder, canLoadMore, loadOlder };
}
